import React, { useCallback, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import CategoryIconWidget from '~/components/Common/Forms/widgets/CategoryIconWidget';

import { useUpdateBundleOffer } from '~/hooks/apis/accommodation/useBundles';

import { BundleOffer, UpdateBundleOfferPayload } from '~/services/AccommodationService';

interface Props {
  bundleId: string;
  offer: BundleOffer;
  open: boolean;
  onClose: () => void;
}

export default function EditBundleOfferDialog({ bundleId, offer, open, onClose }: Props) {
  const [experienceName, setExperienceName] = useState(offer.experienceName ?? '');
  const [experienceDescription, setExperienceDescription] = useState(offer.experienceDescription ?? '');
  const [productId, setProductId] = useState(offer.productId ?? '');

  const { control, setValue, watch } = useForm({ defaultValues: { icon: offer.icon ?? '' } });
  const icon = watch('icon');

  const updateOfferMutation = useUpdateBundleOffer();

  const hasChanges =
    experienceName !== (offer.experienceName ?? '') ||
    experienceDescription !== (offer.experienceDescription ?? '') ||
    productId !== (offer.productId ?? '') ||
    icon !== (offer.icon ?? '');

  const handleSave = useCallback(() => {
    const payload: UpdateBundleOfferPayload = {};
    if (experienceName !== (offer.experienceName ?? '')) {
      payload.experienceName = experienceName;
    }
    if (experienceDescription !== (offer.experienceDescription ?? '')) {
      payload.experienceDescription = experienceDescription;
    }
    if (productId !== (offer.productId ?? '')) {
      payload.productId = productId;
    }
    if (icon !== (offer.icon ?? '')) {
      payload.icon = icon;
    }

    updateOfferMutation.mutate({ bundleId, offerId: offer.id, payload }, { onSuccess: () => onClose() });
  }, [bundleId, offer, experienceName, experienceDescription, productId, icon, updateOfferMutation, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Experience Offer</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Experience Name"
            value={experienceName}
            onChange={(e) => setExperienceName(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Inclusion Text"
            helperText="Customer-facing inclusion name"
            value={experienceDescription}
            onChange={(e) => setExperienceDescription(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Ticket ID"
            helperText="Experience product/ticket identifier"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            size="small"
            fullWidth
          />
          <CategoryIconWidget control={control} label="Inclusion Icon" name="icon" setValue={setValue} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!hasChanges || updateOfferMutation.isPending}
        >
          {updateOfferMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
